/**
 * Utility functions for SCIM search operations
 * All entity types use the same DisplayName or Id pattern for simple search
 */

/**
 * Escapes special characters in a search term for use in SCIM filter strings
 * SCIM filter values are in double quotes, so quotes inside need to be escaped
 */
export function escapeSearchTerm(searchTerm: string): string {
    return searchTerm.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Processes a search query for use with searchtext parameter
 * If Id is in searchAttributes and the term contains non-alphanumeric characters, quote the value for exact match
 * This is used across multiple components (Groups, Localities, Persons, etc.)
 */
export function processSearchQueryForSearchtext(
    query: string,
    searchAttributes?: string[]
): string | undefined {
    if (!query || !query.trim()) {
        return undefined;
    }

    const trimmedQuery = query.trim();

    // If the user already provided explicit quoting, preserve it.
    // This avoids accumulating quotes across navigation/restore cycles (e.g. "\"ME.55\"" -> "\"\"ME.55\"\"").
    const isAlreadyQuoted =
        (trimmedQuery.length >= 2 && trimmedQuery.startsWith('"') && trimmedQuery.endsWith('"')) ||
        (trimmedQuery.length >= 2 && trimmedQuery.startsWith("'") && trimmedQuery.endsWith("'"));
    if (isAlreadyQuoted) {
        return trimmedQuery;
    }

    // If Id is in searchAttributes and the query is a "token" (no spaces),
    // only auto-quote when there are non-alphanumeric characters.
    // Examples that SHOULD be auto-quoted: ME.55, 9TR8AY4NDEF9.0SYS, 487e2b58-fad3-..., user.name
    // Examples that should NOT be auto-quoted: swinfordc, johnsmith
    if (searchAttributes && searchAttributes.includes('Id') && !trimmedQuery.includes(' ')) {
        const hasNonAlphaNumeric = /[^a-z0-9]/i.test(trimmedQuery);
        if (hasNonAlphaNumeric) {
            return `"${trimmedQuery}"`;
        }
    }

    return trimmedQuery;
}

/**
 * Generates a standard SCIM filter for simple search by display name or ID
 * Used by all entity types: Persons, Buildings, Schools, Classes, ResponsibilityCenters, Groups
 * Searches both DisplayName and Id to allow users to paste part of an ID or type part of a display name
 */
export function generateSimpleSearchFilter(searchTerm: string): string | undefined {
    if (!searchTerm || !searchTerm.trim()) {
        return undefined;
    }

    const trimmedTerm = searchTerm.trim();
    const escapedTerm = escapeSearchTerm(trimmedTerm);

    // Search both DisplayName and Id to allow matching by either field
    return `DisplayName co "${escapedTerm}" or Id co "${escapedTerm}"`;
}

/**
 * Generates a SCIM filter for simple search with entity type constraint
 * Used when a specific entity type is selected (e.g., "Shared Users")
 * Searches both DisplayName and Id to allow users to paste part of an ID or type part of a display name
 */
export function generateSimpleSearchFilterWithEntityType(
    searchTerm: string,
    entityType: string
): string | undefined {
    if (!searchTerm || !searchTerm.trim()) {
        return `EntityType eq "${entityType}"`;
    }

    const trimmedTerm = searchTerm.trim();
    const escapedTerm = escapeSearchTerm(trimmedTerm);

    // Entity type first, then search both DisplayName and Id
    return `EntityType eq "${entityType}" and (DisplayName co "${escapedTerm}" or Id co "${escapedTerm}")`;
}

/**
 * Validates if a search term is valid for SCIM filtering
 */
export function isValidSearchTerm(searchTerm: string): boolean {
    return Boolean(searchTerm && searchTerm.trim().length > 0);
}

/**
 * Configuration for flexible attribute search
 * This is a middle ground between simple search (displayName only) and advanced search (full SCIM filters)
 */
export interface FlexibleSearchConfig {
    /**
     * The search text to search for
     */
    searchText: string;
    /**
     * Optional comma-separated list of attributes to search
     * If not provided, defaults to DisplayName only
     * Example: "DisplayName,UserPrincipalName,Email"
     */
    searchAttributes?: string;
}

/**
 * Creates search parameters for flexible attribute search
 * By default, searchText only searches DisplayName.
 * If searchAttributes is provided, it will search those attributes in addition to (or instead of) DisplayName.
 * 
 * This is designed for tables that need more than simple search but less than full advanced search.
 * 
 * @param config - The search configuration
 * @returns An object with searchtext and searchAttributes parameters ready for API calls
 * 
 * @example
 * // Simple search (DisplayName only)
 * const params = createFlexibleSearchParams({ searchText: "John" });
 * // Returns: { searchtext: "John" }
 * 
 * @example
 * // Multi-attribute search
 * const params = createFlexibleSearchParams({ 
 *   searchText: "John",
 *   searchAttributes: "DisplayName,UserPrincipalName,Email"
 * });
 * // Returns: { searchtext: "John", searchAttributes: "DisplayName,UserPrincipalName,Email" }
 */
export function createFlexibleSearchParams(config: FlexibleSearchConfig): {
    searchtext: string;
    searchAttributes?: string;
} {
    const trimmedText = config.searchText.trim();
    if (!trimmedText) {
        throw new Error('searchText cannot be empty');
    }

    const result: { searchtext: string; searchAttributes?: string } = {
        searchtext: trimmedText
    };

    // If searchAttributes is provided, include it
    // If not provided, backend defaults to DisplayName
    if (config.searchAttributes) {
        const trimmedAttributes = config.searchAttributes.trim();
        if (trimmedAttributes) {
            result.searchAttributes = trimmedAttributes;
        }
    }

    return result;
}